/**
 * gsap-scroll.js — shared bootstrap for GSAP + ScrollTrigger driven sections.
 *
 * Loaded (deferred) AFTER gsap.js and gsap-scrolltrigger.js so window.gsap and
 * window.ScrollTrigger already exist by the time a section's own script runs.
 * Exposes a tiny window.GSAPScroll API so each section file only declares its
 * choreography — the plumbing (plugin registration, reduced-motion, geometry
 * refresh) lives here once.
 *
 *   GSAPScroll.ready(cb)        run cb(gsap, ScrollTrigger) once both are ready
 *   GSAPScroll.reduced()        boolean — live prefers-reduced-motion state
 *   GSAPScroll.onReducedChange  subscribe to reduced-motion toggles mid-session
 *   GSAPScroll.refresh()        debounced ScrollTrigger.refresh() (editor/load)
 */
(function () {
  'use strict';

  if (window.GSAPScroll) return; // include-twice guard (multiple sections per page)

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var registered = false;
  var refreshTimer = null;

  function register() {
    if (registered) return true;
    if (!window.gsap || !window.ScrollTrigger) return false;
    window.gsap.registerPlugin(window.ScrollTrigger);
    // Don't refresh pins when only the mobile URL bar shows/hides (height change)
    // — prevents pinned sections from jumping on phones.
    window.ScrollTrigger.config({ ignoreMobileResize: true });
    registered = true;
    return true;
  }

  // Deferred, ordered loading normally means GSAP is ready immediately; poll on
  // rAF as a safety net (cached/slow execution) without busy-waiting forever.
  function ready(cb) {
    var tries = 0;
    (function attempt() {
      if (register()) { cb(window.gsap, window.ScrollTrigger); return; }
      if (tries++ > 120) return; // ~2s @ 60fps — GSAP didn't load; section stays static
      window.requestAnimationFrame(attempt);
    })();
  }

  function reduced() { return reduceMQ.matches; }

  function onReducedChange(cb) {
    if (typeof reduceMQ.addEventListener === 'function') {
      reduceMQ.addEventListener('change', cb);
    } else if (typeof reduceMQ.addListener === 'function') {
      reduceMQ.addListener(cb); // Safari < 14
    }
  }

  // Late imagery/fonts shift pin geometry; recompute after the window settles
  // or after the theme editor swaps a section's DOM.
  function refresh() {
    if (!window.ScrollTrigger) return;
    if (refreshTimer) window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      window.ScrollTrigger.refresh();
    }, 150);
  }

  window.GSAPScroll = {
    ready: ready,
    reduced: reduced,
    onReducedChange: onReducedChange,
    refresh: refresh
  };

  window.addEventListener('load', refresh);
})();
